"use client";

import { Phone, MessageCircle, FileText, Instagram, Linkedin, MapPin, Mail, Clock } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Button } from "@/components/ui/button";
const serviceLinks = [{
  label: "Fontanería",
  href: "/PlumbingPage"
}, {
  label: "Urgencias y averías",
  href: "/Emergency24HoursPage"
}, {
  label: "Detección de Fugas",
  href: "/LeakDetectionPage"
}, {
  label: "Aire Acondicionado",
  href: "/AirConditioningPage"
}, {
  label: "Piscinas",
  href: "/PoolsPage"
}, {
  label: "Reformas de Baños y Cocinas",
  href: "/RenovationsPage"
}, {
  label: "Mantenimiento",
  href: "/MaintenancePage"
}];
const locationLinks = [{
  label: "Murcia",
  href: "/MurciaPage"
}, {
  label: "Alicante",
  href: "/AlicantePage"
}, {
  label: "Almería",
  href: "/AlmeriaPage"
}, {
  label: "Zonas de Servicio",
  href: "/ServiceZonesPage"
}];
const customerServiceLinks = [{
  label: "Contacto",
  href: "/ContactPage"
}, {
  label: "Solicitar Presupuesto",
  href: "/ContactPage"
}, {
  label: "Preguntas Frecuentes",
  href: "/FrequentlyAskedQuestionsPage"
}, {
  label: "Atención al Cliente",
  href: "/CustomerServicePage"
}, {
  label: "Blog",
  href: "/BlogPage"
}, {
  label: "Tienda Online",
  href: "/ShopPage"
}];
const bottomLegalLinks = [{
  label: "Privacidad",
  href: "/PrivacyPolicyPage"
}, {
  label: "Aviso Legal",
  href: "/LegalNoticePage"
}, {
  label: "Cookies",
  href: "/CookiePolicyPage"
}, {
  label: "Condiciones de Compra",
  href: "/PurchaseConditionsPage"
}, {
  label: "Envíos y Devoluciones",
  href: "/ShippingAndReturnsPage"
}];
export default function Footer() {
  const shouldReduceMotion = useReducedMotion();
  return <footer data-section-id="24622" id="pie-de-página" data-nav="dark" className="dark relative bg-background border-t border-border overflow-hidden py-20 md:py-28 text-foreground">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-center" style={{
      opacity: 0.1
    }}>
        <svg viewBox="0 0 1600 400" preserveAspectRatio="xMidYEnd slice" className="w-full h-[60%]" fill="none" stroke="var(--foreground)" strokeWidth="1.25" strokeLinecap="square" strokeLinejoin="miter">
          <path d="M0 380 L220 260 L360 320 L560 140 L780 300 L980 180 L1180 280 L1360 200 L1600 340 L1600 400 L0 400 Z" />
          <path d="M735 300 L780 260 L825 300 L825 350 L735 350 Z" />
          <path d="M765 350 L765 320 L795 320 L795 350" />
        </svg>
      </div>

      <div className="relative mx-auto max-w-[1360px] px-[clamp(1.25rem,5vw,2.5rem)]">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 12
      }} whileInView={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: [0.22, 0.61, 0.36, 1]
      }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 md:gap-8 xl:gap-10">
            
            {/* COLUMN 1 - MARCA */}
            <div className="flex flex-col">
              <Link to="/" className="inline-flex items-center gap-3 text-foreground w-fit focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px]">
                <WvcLogo className="h-10 w-auto" />
              </Link>
              <h3 className="mt-4 font-serif text-xl font-bold text-foreground">
                Servicios de Reparaciones
              </h3>

              <p className="mt-3 text-[14px] leading-relaxed text-secondary-foreground/80">
                Reparación, mantenimiento e instalaciones para viviendas, comunidades, negocios y propiedades vacacionales en Murcia, Alicante y Almería.
              </p>

              <div className="mt-6 flex items-center gap-3">
                <a href="tel:+34657068444" aria-label="Llamar al teléfono" className="flex size-9 items-center justify-center rounded-lg border border-border bg-card/60 text-foreground hover:text-accent-foreground hover:border-primary/50 transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px]">
                  <Phone className="size-4" />
                </a>
                <Link to="https://wa.me/34657068444" newTab aria-label="Contactar por WhatsApp" className="flex size-9 items-center justify-center rounded-lg border border-border bg-card/60 text-[#25D366] hover:bg-[#25D366]/10 hover:border-[#25D366]/50 transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px]">
                  <MessageCircle className="size-4" />
                </Link>
                <Link to="/ContactPage" aria-label="Solicitar presupuesto" className="flex size-9 items-center justify-center rounded-lg border border-border bg-card/60 text-primary hover:text-primary-foreground hover:bg-primary transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px]">
                  <FileText className="size-4" />
                </Link>
              </div>
            </div>

            {/* COLUMN 2 - SERVICIOS */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-secondary-foreground/70 mb-5">
                Servicios
              </div>
              <ul className="flex flex-col gap-2.5">
                {serviceLinks.map((service, i) => <li key={service.label} data-index={i}>
                    <Link to={service.href} className="text-[14px] text-foreground/90 hover:text-white transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px] rounded-xs">
                      {service.label}
                    </Link>
                  </li>)}
              </ul>
            </div>

            {/* COLUMN 3 - ZONAS */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-secondary-foreground/70 mb-5">
                Zonas de Servicio
              </div>
              <ul className="flex flex-col gap-2.5 mb-6">
                {locationLinks.map((loc, i) => <li key={loc.label} data-index={i}>
                    <Link to={loc.href} className="text-[14px] text-foreground/90 hover:text-white transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px] rounded-xs">
                      {loc.label}
                    </Link>
                  </li>)}
              </ul>
              <p className="text-[13px] leading-relaxed text-secondary-foreground/70 bg-card/40 border border-border/70 p-3 rounded-lg">
                Consulta disponibilidad para tu localidad y tipo de servicio.
              </p>
            </div>

            {/* COLUMN 4 - ATENCIÓN AL CLIENTE */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-secondary-foreground/70 mb-5">
                Atención al Cliente
              </div>
              <ul className="flex flex-col gap-2.5">
                {customerServiceLinks.map((link, i) => <li key={link.label} data-index={i}>
                    <Link to={link.href} className="text-[14px] text-foreground/90 hover:text-white transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px] rounded-xs">
                      {link.label}
                    </Link>
                  </li>)}
              </ul>
            </div>

            {/* COLUMN 5 - CONTACTO */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-secondary-foreground/70 mb-5">
                Contacto
              </div>

              <div className="flex flex-col gap-2.5">
                <a href="tel:+34657068444" aria-label="Llamar ahora" className="font-serif text-lg tracking-tight text-foreground hover:text-primary transition-colors duration-200 flex items-center gap-2 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px] rounded-xs">
                  <Phone className="size-4 shrink-0 text-primary" />
                  <span>+34 657 068 444</span>
                </a>
                <a href="mailto:info@serviciosdereparaciones.com" aria-label="Enviar email" className="text-[13px] text-secondary-foreground/90 hover:text-foreground underline underline-offset-4 decoration-[1px] transition-colors duration-200 flex items-center gap-2 break-all focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px] rounded-xs">
                  <Mail className="size-3.5 shrink-0 text-secondary-foreground/70" />
                  <span>info@serviciosdereparaciones.com</span>
                </a>
                <address className="not-italic text-[13px] text-secondary-foreground/80 flex items-start gap-2 pt-1">
                  <MapPin className="size-3.5 shrink-0 mt-0.5 text-secondary-foreground/70" />
                  <span>La Vía Nueva 69, Totana, Murcia</span>
                </address>

                <div className="text-[13px] text-secondary-foreground/80 leading-snug bg-card/40 p-2.5 rounded-md border border-border/70 flex items-start gap-2 mt-1">
                  <Clock className="size-3.5 shrink-0 mt-0.5 text-secondary-foreground/70" />
                  <span>Lunes a sábado. Respuesta el mismo día en averías urgentes.</span>
                </div>
              </div>

              {/* PENDIENTE: añadir URL real de red social antes de reactivar */}
              {/* <div className="mt-4 flex items-center gap-3">
                <Link
                  to="#"
                  aria-label="Instagram"
                  className="text-foreground/80 hover:text-foreground transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px]"
                >
                  <Instagram className="size-4" strokeWidth={1.75} />
                </Link>
                <Link
                  to="#"
                  aria-label="LinkedIn"
                  className="text-foreground/80 hover:text-foreground transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px]"
                >
                  <Linkedin className="size-4" strokeWidth={1.75} />
                </Link>
               </div> */}

              {/* Action Buttons */}
              <div className="mt-5 flex flex-col gap-2">
                <Button asChild variant="outline" size="default" className="w-full justify-center min-h-11">
                  <a href="tel:+34657068444" aria-label="Llamar ahora">
                    <Phone className="size-3.5" />
                    <span>Llamar ahora</span>
                  </a>
                </Button>

                <Button asChild size="default" className="w-full justify-center min-h-11 bg-[#25D366] hover:bg-[#20bd5a] text-white">
                  <Link to="https://wa.me/34657068444" newTab aria-label="WhatsApp">
                    <MessageCircle className="size-3.5" />
                    <span>WhatsApp</span>
                  </Link>
                </Button>

                <Button asChild size="default" className="w-full justify-center min-h-11 bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs">
                  <Link to="/ContactPage" aria-label="Solicitar Presupuesto">
                    <FileText className="size-3.5" />
                    <span>Solicitar Presupuesto</span>
                  </Link>
                </Button>
              </div>
            </div>

          </div>

          {/* BOTTOM BAR */}
          <div className="mt-16 pt-8 border-t border-border">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <p className="text-[13px] tracking-wide text-secondary-foreground/70">
                {`\u00A9 ${new Date().getFullYear()} Servicios de Reparaciones`}
              </p>

              <nav aria-label="Enlaces legales y de compra" className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] tracking-wide text-secondary-foreground/70">
                {bottomLegalLinks.map((item, i) => <span key={item.label} className="inline-flex items-center gap-3">
                    <Link to={item.href} className="hover:text-foreground transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-[3px] rounded-xs">
                      {item.label}
                    </Link>
                    {i < bottomLegalLinks.length - 1 && <span className="text-secondary-foreground/50" aria-hidden="true">
                        {"\u00B7"}
                      </span>}
                  </span>)}
              </nav>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Mobile Floating WhatsApp Button */}
      <Button asChild className="fixed bottom-5 right-5 z-50 size-14 rounded-lg bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-lg lg:hidden p-0 flex items-center justify-center">
        <Link to="https://wa.me/34657068444" newTab aria-label="Escríbenos por WhatsApp">
          <MessageCircle className="size-6 text-white" />
        </Link>
      </Button>
    </footer>;
}